import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";
import { App } from "vue";
import { i18n } from "./i18n";

export default {
  install: (app: App) => {
    const config: CookieConsent.CookieConsentConfig = {
      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {},
      },
      guiOptions: {
        consentModal: {
          equalWeightButtons: false,
        },
        preferencesModal: {
          equalWeightButtons: false,
        },
      },

      language: {
        // @ts-expect-error: i18n.global.locale.value is not typed
        default: i18n.global.locale.value,
        translations: {
          "en-GB": {
            consentModal: {
              title: "We use cookies",
              description:
                "Cookies help us address issues and improve your basecamp experience. " +
                'Find more information in our <a href="https://yetipay.me/privacy/" target="_blank">privacy policy</a>.',
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              showPreferencesBtn: "Manage cookies",
            },
            preferencesModal: {
              title: "Manage cookie preferences",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Accept current selection",
              closeIconLabel: "Close modal",
              sections: [
                {
                  title: "How do we use cookies?",
                  description:
                    "We only use cookies where we need them, to enhance your basecamp experience or help us analyse any issues.",
                },
                {
                  title: "Strictly necessary cookies",
                  description:
                    "These cookies are essential for the proper functioning of basecamp and cannot be disabled.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Performance and analytics",
                  description:
                    "These cookies collect information about how you use our website. " +
                    "All of the data is anonymized and cannot be used to identify you.",
                  linkedCategory: "analytics",
                },
                {
                  title: "More information",
                  description:
                    "For any queries in relation to my policy on cookies and your choices, " +
                    'read our <a href="https://yetipay.me/cookies/" target="_blank">Cookie Policy</a> ' +
                    'or please email <a href="mailto:help@yetipay.me">help@yetipay.me</a>.',
                },
              ],
            },
          },
          "es-ES": {
            consentModal: {
              title: "Utilizamos cookies",
              description:
                "Las cookies nos ayudan a solucionar problemas y mejorar tu experiencia en basecamp. " +
                'Encuentra más información en nuestra <a href="https://yetipay.me/privacy/" target="_blank">política de privacidad</a>.',
              acceptAllBtn: "Aceptar todo",
              acceptNecessaryBtn: "Rechazar todo",
              showPreferencesBtn: "Gestionar cookies",
            },
            preferencesModal: {
              title: "Gestionar preferencias de cookies",
              acceptAllBtn: "Aceptar todo",
              acceptNecessaryBtn: "Rechazar todo",
              savePreferencesBtn: "Aceptar selección actual",
              closeIconLabel: "Cerrar modal",
              sections: [
                {
                  title: "Cómo utilizamos las cookies?",
                  description:
                    "Solo utilizamos cookies cuando las necesitamos, para mejorar tu experiencia en basecamp o ayudarnos a analizar problemas.",
                },
                {
                  title: "Cookies estrictamente necesarias",
                  description:
                    "Estas cookies son esenciales para el correcto funcionamiento de basecamp y no se pueden deshabilitar.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Rendimiento y análisis",
                  description:
                    "Estas cookies recopilan información sobre cómo utilizas nuestro sitio web. " +
                    "Todos los datos son anónimos y no se pueden utilizar para identificarte.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Más información",
                  description:
                    "Para cualquier consulta relacionada con nuestra política de cookies y tus opciones, " +
                    'lee nuestra <a href="https://yetipay.me/cookies/" target="_blank">Política de Cookies</a> ' +
                    'o envía un correo electrónico a <a href="mailto:help@yetipay.me">help@yetipay.me</a>.',
                },
              ],
            },
          },
          "it-IT": {
            consentModal: {
              title: "Utilizziamo i cookie",
              description:
                "I cookie ci aiutano a risolvere problemi e migliorare la tua esperienza su basecamp. " +
                'Trova maggiori informazioni nella nostra <a href="https://yetipay.me/privacy/" target="_blank">politica sulla privacy</a>.',
              acceptAllBtn: "Accetta tutti",
              acceptNecessaryBtn: "Rifiuta tutti",
              showPreferencesBtn: "Gestisci cookie",
            },
            preferencesModal: {
              title: "Gestisci preferenze cookie",
              acceptAllBtn: "Accetta tutti",
              acceptNecessaryBtn: "Rifiuta tutti",
              savePreferencesBtn: "Accetta selezione corrente",
              closeIconLabel: "Chiudi finestra",
              sections: [
                {
                  title: "Come utilizziamo i cookie?",
                  description:
                    "Utilizziamo i cookie solo quando necessario, per migliorare la tua esperienza su basecamp o aiutarci ad analizzare eventuali problemi.",
                },
                {
                  title: "Cookie strettamente necessari",
                  description:
                    "Questi cookie sono essenziali per il corretto funzionamento di basecamp e non possono essere disattivati.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Performance e analisi",
                  description:
                    "Questi cookie raccolgono informazioni su come utilizzi il nostro sito web. " +
                    "Tutti i dati sono anonimi e non possono essere utilizzati per identificarti.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Ulteriori informazioni",
                  description:
                    "Per qualsiasi domanda relativa alla nostra politica sui cookie e alle tue scelte, " +
                    'leggi la nostra <a href="https://yetipay.me/cookies/" target="_blank">Politica sui Cookie</a> ' +
                    'o invia una email a <a href="mailto:help@yetipay.me">help@yetipay.me</a>.',
                },
              ],
            },
          },
          "fr-FR": {
            consentModal: {
              title: "Nous utilisons des cookies",
              description:
                "Les cookies nous aident à résoudre les problèmes et à améliorer votre expérience basecamp. " +
                'Trouvez plus d\'informations dans notre <a href="https://yetipay.me/privacy/" target="_blank">politique de confidentialité</a>.',
              acceptAllBtn: "Tout accepter",
              acceptNecessaryBtn: "Tout refuser",
              showPreferencesBtn: "Gérer les cookies",
            },
            preferencesModal: {
              title: "Gérer les préférences de cookies",
              acceptAllBtn: "Tout accepter",
              acceptNecessaryBtn: "Tout refuser",
              savePreferencesBtn: "Accepter la sélection actuelle",
              closeIconLabel: "Fermer la fenêtre",
              sections: [
                {
                  title: "Comment utilisons-nous les cookies ?",
                  description:
                    "Nous n'utilisons les cookies que lorsque nous en avons besoin, pour améliorer votre expérience basecamp ou nous aider à analyser les problèmes.",
                },
                {
                  title: "Cookies strictement nécessaires",
                  description:
                    "Ces cookies sont essentiels au bon fonctionnement de basecamp et ne peuvent pas être désactivés.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Performance et analyse",
                  description:
                    "Ces cookies collectent des informations sur votre utilisation de notre site web. " +
                    "Toutes les données sont anonymes et ne peuvent pas être utilisées pour vous identifier.",
                  linkedCategory: "analytics",
                },
                {
                  title: "Plus d'informations",
                  description:
                    "Pour toute question concernant notre politique en matière de cookies et vos choix, " +
                    'lisez notre <a href="https://yetipay.me/cookies/" target="_blank">Politique de Cookies</a> ' +
                    'ou envoyez un email à <a href="mailto:help@yetipay.me">help@yetipay.me</a>.',
                },
              ],
            },
          },
        },
      },

      onConsent: () => {
        if (!CookieConsent.acceptedCategory("analytics")) {
          app.config.globalProperties.$posthog.opt_out_capturing();
        } else {
          app.config.globalProperties.$posthog.opt_in_capturing();
        }
      },
    };
    CookieConsent.run(config);
    app.config.globalProperties.$CookieConsent = CookieConsent;
    app.provide("CookieConsent", CookieConsent);
  },
};
